/**
 * Presentation logic for the community feedback layer (Contract 8, issues #617/#618).
 *
 * Everything user-visible about a tally is derived here rather than in the
 * components, because the same tally is worded on TWO surfaces (catalog cards and
 * the detail page — not list rows, per #617's criterion). Adoption learned this the
 * hard way: a figure shown in more than one place from one source can only diverge
 * through a rendering bug, so the derivation lives once and the components render
 * what they are given.
 *
 * The vote-count gate is Contract 8's MIN_VOTES_FOR_RATIO, deliberately not a
 * local `5`: the same threshold governs the display copy here (#620) and the
 * Recommended sort's neutral fallback (#619). The prototype hardcoded it in both
 * places, which is exactly how a display gate and a ranking gate drift apart.
 */

import {
  MIN_VOTES_FOR_RATIO,
  type FeedbackTally,
  type ReasonCode,
  type VoteDirection,
} from "@skills-marketplace/contracts/contract-8-feedback";

/**
 * What a surface should render for an entry's usefulness.
 *
 * A discriminated union rather than a formatted string so each surface can make
 * its own layout call (see `unvoted` below) while the *copy* stays centralised.
 */
export type UsefulnessDisplay =
  /**
   * No votes at all.
   *
   * Reads IDENTICALLY to `insufficient` — both show "Not enough votes yet" — because
   * #620's criterion is "fewer than 5 total votes" with no carve-out for zero, and
   * zero is fewer than five.
   *
   * The prototype does distinguish them (its card helper returns "" at zero while its
   * detail block shows the message), and an earlier revision here followed that on the
   * reasoning that ~1500 cards repeating one disclaimer is noise. Overruled by the
   * written criterion: a card that shows nothing is indistinguishable from a card
   * whose signal failed to load, whereas the message says "asked and answered: not
   * yet".
   *
   * Kept as a SEPARATE state from `insufficient` even so, because the detail page must
   * suppress its "N votes so far" sub-line here — "0 votes so far" is noise where
   * "3 votes so far" is information.
   */
  | { kind: "unvoted" }
  /**
   * Between 1 and MIN_VOTES_FOR_RATIO - 1 votes: too few to quote a ratio.
   *
   * #620: a single up-vote would otherwise read "100% useful", which is more
   * confident than one vote earns. Suppressing the number also stops a lightly
   * voted entry from reading as bad when it is merely new.
   */
  | { kind: "insufficient"; total: number }
  /** At or above the gate — the ratio is worth quoting. */
  | { kind: "rated"; useful: number; total: number };

/**
 * Derive the display state for an entry's tally.
 *
 * `undefined` means the entry has no tally in the artifact/response at all, which
 * is the same user-facing state as a tally of zero votes — absence and zero are
 * ONE state here, so callers never need to distinguish them.
 */
export function usefulnessDisplay(tally: FeedbackTally | undefined): UsefulnessDisplay {
  if (!tally || tally.total <= 0) return { kind: "unvoted" };
  if (tally.total < MIN_VOTES_FOR_RATIO) {
    return { kind: "insufficient", total: tally.total };
  }
  return { kind: "rated", useful: tally.useful, total: tally.total };
}

/**
 * The visible label for a display state. Never empty: every entry says something,
 * because "nothing rendered" and "the signal failed to load" look the same to a reader.
 *
 * "N of M found useful" is the validated wording. Note that M is the TOTAL vote
 * count, not a negative tally — #617 forbids ever displaying a negative count,
 * and this does not: it reports how many people voted, and how many of those
 * found the entry useful.
 */
export function usefulnessLabel(display: UsefulnessDisplay): string {
  switch (display.kind) {
    // Zero and 1-4 read the same, per #620's "fewer than 5 total votes".
    case "unvoted":
    case "insufficient":
      return "Not enough votes yet";
    case "rated":
      return formatUsefulOfTotal(display.useful, display.total, "");
  }
}

/**
 * Headline wording for the detail page's summary block.
 *
 * Deliberately a separate function from `usefulnessLabel`, not a duplicate: the
 * detail page reads "N of M found this useful" (a sentence, with room to breathe)
 * while a dense card reads "N of M found useful". Both derive from one display
 * state, so the NUMBERS cannot diverge even though the phrasing differs.
 *
 * Unlike the card, this never returns "" — the detail page is where the vote
 * prompt lives, so an unvoted entry still needs a headline to sit above it.
 */
export function usefulnessHeadline(display: UsefulnessDisplay): string {
  switch (display.kind) {
    case "unvoted":
    case "insufficient":
      return "Not enough votes yet";
    case "rated":
      return formatUsefulOfTotal(display.useful, display.total, "this ");
  }
}

/**
 * Shared phrasing for both `usefulnessLabel` and `usefulnessHeadline`, so the two
 * public wordings cannot drift on anything but which word comes before "useful".
 */
function formatUsefulOfTotal(useful: number, total: number, thisWord: "" | "this "): string {
  return `${useful} of ${total} found ${thisWord}useful`;
}

/**
 * The author-facing headline for My Skills (#621 review) — ALWAYS the "N of M" shape,
 * never gated by MIN_VOTES_FOR_RATIO. That gate exists to protect the PUBLIC catalog
 * from a single vote reading as a rating; My Skills' audience is the entry's own
 * owner, who has the most reason to see the full picture and whom #620's rationale was
 * never written to protect from. Previously this row showed a bare `{useful} found
 * useful` count with no denominator, which (a) read as broken for a down-vote-only
 * plugin ("0 found useful" with no context) and (b) showed the AUTHOR less than a
 * random catalog visitor sees once MIN_VOTES_FOR_RATIO is met. Shares
 * `formatUsefulOfTotal` with the public headline so the wording can't drift, only
 * whether a ratio is shown at all.
 */
export function authorUsefulnessHeadline(useful: number, total: number): string {
  return formatUsefulOfTotal(useful, total, "this ");
}

/**
 * Supplementary hover/description text, or "" when there is nothing to add.
 *
 * For `insufficient` this is the only place the raw vote count appears — enough
 * for a curious reader to see that votes exist without the headline implying a
 * rating has been earned.
 */
export function usefulnessTooltip(display: UsefulnessDisplay): string {
  switch (display.kind) {
    case "unvoted":
      return "";
    case "insufficient":
      return `${display.total} ${plural(display.total, "vote")} so far — too few to show a rating`;
    case "rated":
      return `${display.useful} of ${display.total} found this useful`;
  }
}

function plural(n: number, word: string): string {
  return n === 1 ? word : `${word}s`;
}

// ── Reason chip labels ─────────────────────────────────────

/**
 * Display text for every reason code, keyed by the code.
 *
 * APPEND-ONLY. A chip retired from the offered set stays here forever, because
 * historical votes still carry its code and the contributor's dashboard still has
 * to render them. Removing an entry here does not clean anything up — it breaks
 * the display of votes already cast.
 *
 * Rewording is free and expected: the code is the meaning, this map is cosmetics.
 * That separation is the whole reason Contract 8 stores codes rather than labels.
 */
export const REASON_LABELS: Record<ReasonCode, string> = {
  SAVED_TIME: "Saved me time",
  CLEAR_INSTRUCTIONS: "Clear instructions",
  WORKED_AS_EXPECTED: "Worked as expected",
  FIT_MY_USE_CASE: "Fit my exact use case",
  DID_NOT_WORK: "Didn't work as expected",
  UNCLEAR_INSTRUCTIONS: "Unclear instructions",
  MISSING_SOMETHING: "Missing something I needed",
  OUT_OF_DATE: "Out of date",
};

/**
 * Label for a code, falling back to the raw code for anything unrecognised.
 *
 * The fallback matters: a vote cast against a newer deployment's code could reach
 * an older client. Rendering the raw code is ugly but honest — far better than
 * `undefined` appearing in a contributor's feedback list.
 */
export function reasonLabel(code: ReasonCode): string {
  return REASON_LABELS[code] ?? code;
}

// ── Fixed copy ─────────────────────────────────────────────

/**
 * Copy lifted verbatim from the validated prototype. Held here so the privacy
 * promises in particular are stated identically everywhere they appear — these
 * are commitments to the voter, not decoration, and #506 puts "written reasons
 * stay private to the contributor" in scope while ruling a public review feed out.
 *
 * Note the heading is the same for both directions. The prototype asks a single
 * "Why?" either way; only the chip SET differs by direction.
 */
export const FEEDBACK_COPY = {
  votePrompt: "Was this skill useful?",
  voteUseful: "Useful",
  voteNotUseful: "Not useful",

  reasonHeading: "Why?",
  reasonHint: "(optional, pick any that apply — shared privately with the contributor, not posted publicly)",
  reasonOther: "Other",
  reasonOtherPlaceholder: "Say more, in your own words (140 characters max)",
  submit: "Submit",
  skip: "Skip",

  thanks: "Thanks for the feedback",
  // No "...with a reason" variant: privacyNote in VoteBlock already states, permanently,
  // that written feedback goes privately to the contributor. A headline saying it again
  // put the same sentence on screen twice (#571 review).
  countedAnonymously: "Your response is counted anonymously.",
  undo: "Undo",

  privacyNote:
    "Additional feedback is shared privately with the contributor in their My Skills dashboard. It is not posted publicly or moderated.",
} as const;

/** Confirmation line after voting, echoing the direction back to the voter. */
export function voteConfirmation(direction: VoteDirection): string {
  return direction === "up" ? "You marked this useful." : "You marked this not useful.";
}
